/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { useContext } from "react";
import { getStudyEUnityToken } from "../../pages/study/ducks/operators";
import EUnityWindowContext from "../../context/EUnityWindowContext";
import { EUnityToken } from "../../types/integrations";
import { formatRegionUrl, HttpApiError, IErrorMessage } from "@advapacs/common";
import { useAppDispatch } from "../../config/store";
import { StudyAction } from "../../pages/study/ducks/studySlice";
import { isMobile, isSafari } from "react-device-detect";

// ============================= HOOK ==============================
const useOpenEUnity = (): ((studies: string[]) => Promise<void>) => {
  // ============================= CONTEXT ==============================
  const eunityWindow = useContext(EUnityWindowContext);
  const dispatch = useAppDispatch();

  // ============================= HELPERS ==============================
  const getUrl = (token: EUnityToken): string => {
    let region: string | null = "";
    const searchParams = new URLSearchParams(token.launchParams);
    if (!region) {
      // Region is resolved when the application first loads and cached in local storage.
      // There is no need to resolve it again.
      region = localStorage.getItem("region");
    }

    if (!region) {
      // eslint-disable-next-line no-console
      console.log("[EUNITY]: Cannot find the region in the cache");
      return "";
    }

    const baseURL = formatRegionUrl(region, "eunity").replace(".clinician", "");
    return `${baseURL}/e/viewer?${searchParams.toString()}`;
  };

  const redirectToNewUrl = (windowUrl: string) => {
    eunityWindow.eunityWindow.location.href = windowUrl;
  };

  const openViewerWithNewWindow = (windowUrl: string) => {
    if (!eunityWindow.eunityWindow || eunityWindow.eunityWindow?.closed) {
      // eslint-disable-next-line no-console
      console.log("[EUNITY]: Open Viewer.");
      eunityWindow.eunityWindow = window.open(windowUrl, "EUnityViewer");
    } else {
      // eslint-disable-next-line no-console
      console.log("[EUNITY]: Redirect new URL.");
      redirectToNewUrl(windowUrl);
    }
  };

  // ============================= FUNCTIONS ==============================
  const openEUnity = (tokens: EUnityToken[]) => {
    const tokenObject = tokens[0] ?? null;
    if (!tokenObject) {
      // eslint-disable-next-line no-console
      console.log("[EUNITY]: can't open window without token.");
      return;
    }
    openViewerWithNewWindow(getUrl(tokens[0]));
  };

  const openEmptyWindow = (): Promise<void> => {
    if (!(isSafari && isMobile)) {
      // prevent open new empty window if browser is not safari
      // eslint-disable-next-line no-console
      console.log(
        "[EUNITY]: not allow open new empty window for when browser is not safari and mobile device."
      );
      return Promise.resolve();
    }
    // validate window
    if (!eunityWindow.eunityWindow || eunityWindow.eunityWindow?.closed) {
      openViewerWithNewWindow("");
    } else {
      // eslint-disable-next-line no-console
      console.log("[EUNITY]: This window already opened.");
    }
    return new Promise<void>((resolve) => {
      setTimeout(resolve, 500);
    });
  };

  // ============================= ROOT ==============================
  return async (studyIds: string[]) => {
    try {
      await openEmptyWindow();
      const token = await getStudyEUnityToken(studyIds);
      openEUnity(token?.data.tokens);
    } catch (errorResponse) {
      const error = (errorResponse as IErrorMessage)?.response
        ?.data as HttpApiError;
      if (error.code === "1017") {
        dispatch(
          StudyAction.setShowPatientVerifySmsModal({ show: true, studyIds })
        );
      }
    }
  };
};

export default useOpenEUnity;
